import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-circle.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import { ReactComponent as SuccessIcon } from '../../../../assets/icons/success-tick.svg';
import { ReactComponent as WarningIcon } from '../../../../assets/icons/warning.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import { useAppDispatch } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
// import { fetchRequestApproved } from '../../../../redux/slice/RequestApprovedSlice';
// import { fetchRequestRejected } from '../../../../redux/slice/RequestRejectedSlice';
import { fetchRequestPending } from '../../../../redux/slice/RequestPendingSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import CustomDatePicker from '../../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../../shared-components/multi-select-field/MultiSelectField';
import RejectReasonField from '../../../../shared-components/reject-reason/RejectReasonField';
import SelectFiled from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import ConvertDDMMYYYYtoISO from '../../../../shared-functions/ConvertDDMMYYYYtoISO';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { stringValidation } from '../../../../shared-functions/Validation';
import penaltyManagementApi from '../../../../utils/api/penalty-management';
import AuditLog from '../AuditLog';

interface UserAddInterface {
  accountCustomId: string;
  ceilingType: string;
  ceilingValue: number;
  triggerMessage: string[];
  effectiveFrom: string;
  effectiveEnd: string;
  dynamicPricing: string[];
  stationId: string[];
  stationNames: string[];
  durationAmountList: DurationAmountListInterface[];
  penaltyFeature: boolean;
  facilityRequest: boolean;
}

interface DurationAmountListInterface {
  from: string;
  to: string;
  mode: string;
  amount: string;
}

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * Normalize a value to an array
 * @param value - string or string array
 * @returns - array of strings
 */
const normalizeToArray = (value?: string | string[]): string[] => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

/**
 * Edit Penalty
 * @returns
 */
function ViewOverrides({
  closeModal,
  currentData,
  type,
  hasEdit,
  handleRefresh,
}: {
  closeModal: (value: string) => void;
  currentData?: any;
  type: 'approved' | 'pending' | 'rejected';
  hasEdit?: boolean;
  handleRefresh?: () => void;
}) {
  const [ruleErrors, setRuleErrors] = useState<{
    [index: number]: { from: boolean; to: boolean };
  }>({});
  const { t, i18n } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [buttonStatus, setButtonStatus] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  // const [rejectError, setRejectError] = useState<string>('');
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });
  const [penaltyDetails, setPenaltyDetails] = useState<UserAddInterface>({
    accountCustomId: '',
    ceilingType: '',
    ceilingValue: 0,
    triggerMessage: [],
    effectiveFrom: '',
    effectiveEnd: '',
    dynamicPricing: [],
    stationId: [],
    stationNames: [],
    durationAmountList: [{ from: '', to: '', mode: '', amount: '' }],
    penaltyFeature: false,
    facilityRequest: false,
  });

  /**
   * Normal Input Handler
   */
  const handleInputChange = (name: string, value: string | number | boolean | string[]) => {
    setPenaltyDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const allModes = [
    { label: t('grace'), value: 'grace' },
    { label: t('flatRate'), value: 'flatRate' },
    { label: t('perMinute'), value: 'perMinute' },
  ];

  /**
   * Update rule row field value
   */
  const updateRule = (index: number, name: keyof DurationAmountListInterface, value: string) => {
    const rules = [...penaltyDetails.durationAmountList];

    rules[index][name] = value;
    // ✅ If mode is GRACE → force amount = "0"
    if (name === 'mode' && value === 'grace') {
      rules[index].amount = '0';
    }

    // ✅ If switching away from grace → clear amount
    if (name === 'mode' && value !== 'grace') {
      rules[index].amount = '';
    }
    setPenaltyDetails((previous) => ({
      ...previous,
      rules,
    }));
  };

  /**
   * Filter available mode options based on already selected modes
   */
  const getAvailableModes = (currentIndex: number) => {
    const selectedModes = penaltyDetails.durationAmountList.map((r) => r.mode).filter(Boolean);

    return allModes.filter((m) => {
      // Allow any mode if it's already selected for the current index (editing case)
      if (penaltyDetails.durationAmountList[currentIndex].mode === m.value) return true;
      // Make "Grace" unique: if already selected anywhere else, hide it
      if (m.value === 'grace' && selectedModes.includes('grace')) {
        return false;
      }

      // Allow Flat Rate & Per Minute multiple times
      return true;
    });
  };

  // /**
  //  * handle Action Reject
  //  */
  // const handleActionReject = async (reason: string, data: any) => {
  //   try {
  //     await penaltyManagementApi.createApprovedRejectedPermission({
  //       status: 'rejected',
  //       reason,
  //       penaltyIds: [data._id],
  //     });
  //     dispatch(fetchRequestPending({ page: DefaultApiLimit.page, limit: 1 }));

  //     dispatch(fetchRequestApproved({ page: DefaultApiLimit.page, limit: 1 }));
  //     dispatch(fetchRequestRejected({ page: DefaultApiLimit.page, limit: 1 }));
  //   } catch (error: any) {
  //     dispatch(
  //       setNotificationDetails({
  //         type: 'error',
  //         notificationDetails: {
  //           statusCode: error?.response?.data?.code || 404,
  //           message: error?.response?.data?.message || 'Something went wrong',
  //         },
  //       }),
  //     );
  //   }
  // };

  // /**
  //  * handle Action Approve
  //  */
  // const handleActionApprove = async (selectedItems: string) => {
  //   try {
  //     await penaltyManagementApi.createApprovedRejectedPermission({
  //       status: 'approved',
  //       penaltyIds: [selectedItems],
  //     });

  //     dispatch(fetchRequestPending({ page: DefaultApiLimit.page, limit: 1 }));

  //     dispatch(fetchRequestApproved({ page: DefaultApiLimit.page, limit: 1 }));
  //     dispatch(fetchRequestRejected({ page: DefaultApiLimit.page, limit: 1 }));
  //   } catch (error: any) {
  //     dispatch(
  //       setNotificationDetails({
  //         type: 'error',
  //         notificationDetails: {
  //           statusCode: error?.response?.data?.code || 404,
  //           message: error?.response?.data?.message || 'Something went wrong',
  //         },
  //       }),
  //     );
  //   }
  // };

  /**
   * Rules Valid
   */
  const isRulesValid = (): boolean => {
    let valid = true;
    const errors: {
      [index: number]: { from: boolean; to: boolean };
    } = {};

    penaltyDetails.durationAmountList.forEach((rule, index) => {
      const from = Number(rule.from);
      const to = Number(rule.to);

      let fromError = false;
      let toError = false;

      // 1️ Empty checks
      if (rule.from === '') fromError = true;
      if (rule.to === '') toError = true;

      // 2️ Same value (10–10)
      if (rule.from !== '' && rule.to !== '' && from === to) {
        toError = true;
      }

      // 3️ to < from
      if (rule.from !== '' && rule.to !== '' && to < from) {
        fromError = true;
        toError = true;
      }

      if (index > 0) {
        const previousTo = Number(penaltyDetails.durationAmountList[index - 1].to);

        if (rule.from !== '' && !Number.isNaN(previousTo) && from !== previousTo) {
          fromError = true; // ❌ breaks chain
        }
      }

      if (fromError || toError) {
        valid = false;
        errors[index] = { from: fromError, to: toError };
      }

      // 5️⃣ Mode validation
      if (!rule.mode) valid = false;

      // 6️⃣ Amount validation
      if (rule.mode !== 'grace' && rule.amount === '') valid = false;
    });

    setRuleErrors(errors);
    return valid;
  };
  const isDateInvalid =
    penaltyDetails.effectiveFrom &&
    penaltyDetails.effectiveEnd &&
    new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom)) >
      new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveEnd));

  /**
   * Submit payload to API
   * @returns
   */
  const handleSubmit = async () => {
    setButtonStatus(true);
    const isFormInvalid =
      penaltyDetails.ceilingType === '' ||
      !stringValidation(1, 100, penaltyDetails.ceilingType) ||
      penaltyDetails.ceilingValue === 0 ||
      penaltyDetails.ceilingValue === ('' as any) ||
      penaltyDetails.dynamicPricing.length === 0 ||
      penaltyDetails.triggerMessage.length === 0 ||
      penaltyDetails.effectiveFrom === '' ||
      penaltyDetails.effectiveEnd === '' ||
      new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom)) >
        new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveEnd)) ||
      !isRulesValid();

    if (isFormInvalid) {
      setErrorMessage(true);
      setButtonStatus(false);
      return;
    }

    try {
      const stationId = isEdit && currentData.stationId;
      const payload = {
        // accountId: override.accountId,
        stationId,
        dynamicPricing: penaltyDetails.dynamicPricing,
        ceilingType: penaltyDetails.ceilingType,
        ceilingValue: Number(penaltyDetails.ceilingValue),
        triggerMessage: penaltyDetails.triggerMessage,
        effectiveFrom: ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom),
        effectiveTo: ConvertDDMMYYYYtoISO(penaltyDetails.effectiveEnd),
        durationAmountList: penaltyDetails.durationAmountList,
      };
      await penaltyManagementApi.rejectedResubmit(currentData._id, payload);
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: '',
        rightCorner: '',
        content: (
          <SuccessNotification
            title={t('overrideUpdatedSuccess')}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalBoxStatus('close');
              closeModal('success');
              dispatch(
                fetchRequestPending({ page: DefaultApiLimit.page, limit: DefaultApiLimit.limit }),
              );
              if (handleRefresh) handleRefresh();
            }}
          />
        ),
        notificationModal: true,
      });
    } catch (error: any) {
      setButtonStatus(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 404,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * Delete rule row
   */
  const deleteRule = (index: number) => {
    setPenaltyDetails((previous) => ({
      ...previous,
      durationAmountList: previous.durationAmountList.filter((_, input) => input !== index),
    }));
  };

  /**
   * Add rule row
   */
  const addRule = () => {
    const index = penaltyDetails.durationAmountList.length - 1;
    const lastRule = penaltyDetails.durationAmountList[index];
    const from = Number(lastRule.from);
    const to = Number(lastRule.to);
    let fromError = false;
    let toError = false;

    // Case 1: Empty values
    if (lastRule.from === '') {
      fromError = true;
    }

    if (lastRule.to === '') {
      toError = true;
    }
    if (lastRule.to === '' || to <= from) {
      toError = true;
    }
    // Case 2: Range invalid
    if (lastRule.from !== '' && lastRule.to !== '' && to < from) {
      fromError = true;
      toError = true;
    }

    // If any error → show error only for this row
    if (fromError || toError) {
      setRuleErrors((previous) => ({
        ...previous,
        [index]: { from: fromError, to: toError },
      }));
      return;
    }

    // Clear error if valid
    setRuleErrors((previous) => ({
      ...previous,
      [index]: { from: false, to: false },
    }));

    // If valid → create new rule
    setPenaltyDetails((previous) => ({
      ...previous,
      durationAmountList: [
        ...previous.durationAmountList,
        {
          from: String(to), // next rule starts from previous TO
          to: '',
          mode: '',
          amount: '',
        },
      ],
    }));
  };

  useEffect(() => {
    setIsLoading(true);
    if (!currentData._id) {
      setIsLoading(false);
      return;
    }
    penaltyManagementApi.viewSingleAccountDetails(currentData._id).then((response) => {
      const data = response.data || {};
      setPenaltyDetails({
        accountCustomId: data.accountCustomId ?? '',
        stationId: normalizeToArray(data.stationId),
        stationNames: normalizeToArray(data.stationNames),
        ceilingType: data.ceilingType ?? '',
        ceilingValue: data.ceilingValue ?? 0,
        triggerMessage: normalizeToArray(data.triggerMessage),
        dynamicPricing: normalizeToArray(data.dynamicPricing),
        effectiveFrom: ConvertUTCtoDate(data.effectiveFrom) ?? '',
        effectiveEnd: ConvertUTCtoDate(data.effectiveTo) ?? '',
        durationAmountList:
          Array.isArray(data.durationAmountList) && data.durationAmountList.length
            ? data.durationAmountList.map((item: any) => ({
                from: String(item.from),
                to: String(item.to),
                mode: item.mode,
                amount: String(item.amount),
              }))
            : [{ from: '', to: '', mode: '', amount: '' }],
        penaltyFeature: Boolean(data.penaltyFeature),
        facilityRequest: Boolean(data.facilityRequest),
      });
      setRejectReason(data.requestStatus);
      setIsLoading(false);
    });
  }, [currentData._id]);

  if (isLoading) {
    return (
      <div className="loader">
        <BatteryLoader />
      </div>
    );
  }

  return (
    <div className="override-wrap" dir={i18n.dir()}>
      <p className={`heading ${isEdit ? 'edit' : type}`}>
        {isEdit ? t('editOverride') : t(type)}
        {type === 'rejected' && !isEdit && <CloseIcon />}
        {type === 'approved' && <SuccessIcon className="icon" />}
        {type === 'pending' && <WarningIcon className="icon" />}
      </p>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        // rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="override-container view">
        <div className="override-left">
          {hasEdit && (
            <div className="edit-icon-wrap">
              <EditIcon className="edit-icon" onClick={() => setIsEdit(true)} />
            </div>
          )}
          <div className="inputs-container">
            <CustomInput
              name="accountId"
              inputLabel={t('accountId')}
              type="text"
              placeholder={t('enterValue')}
              value={penaltyDetails.accountCustomId}
              onChangeValue={handleInputChange}
              error={errorMessage && penaltyDetails.accountCustomId === ''}
              errorMessage={t('required')}
              disabled
              valueRequired
            />
            <div className="input-side-by-side">
              <MultiSelectDropdown
                name="stationId"
                title={t('stationId')}
                selectedValues={penaltyDetails.stationId}
                options={penaltyDetails?.stationId?.map((station) => ({
                  label: station,
                  value: station,
                }))}
                valueRequired
                onSelectionChange={handleInputChange}
                placeholder={t('selectStation')}
                placeholderFilled={t('selectStation')}
                error={errorMessage || penaltyDetails.stationId.length === 0}
                errorMessage={t('required')}
                disabled
              />
              <MultiSelectDropdown
                name="dynamicPricing"
                title={t('dynamicPricing')}
                selectedValues={penaltyDetails.dynamicPricing}
                onSelectionChange={handleInputChange}
                placeholder={t('selectOption')}
                placeholderFilled={t('options')}
                options={[
                  { label: t('base'), value: 'base' },
                  { label: t('peak'), value: 'peak' },
                  { label: t('offPeak'), value: 'offpeak' },
                ]}
                search={false}
                multiSelectDisable={false}
                valueRequired
                fontSize="small"
                error={errorMessage && penaltyDetails.dynamicPricing.length === 0}
                errorMessage={t('required')}
                disabled={!isEdit}
              />
              <SelectFiled
                name="ceilingType"
                inputLabel={t('ceilingType')}
                selectedValue={penaltyDetails.ceilingType}
                placeholder={t('selectCeilingType')}
                onChangeValue={handleInputChange}
                valueRequired
                error={errorMessage && penaltyDetails.ceilingType === ''}
                errorMessage={t('required')}
                options={[
                  { label: t('fixedAmount'), value: 'fixedAmount' },
                  { label: t('percentageOfBase'), value: 'percentage' },
                ]}
                disabled={!isEdit}
              />
              <CustomInput
                name="ceilingValue"
                inputLabel={t('maxCeilingValue')}
                type="number"
                placeholder={t('enterValue')}
                value={penaltyDetails.ceilingValue}
                onChangeValue={handleInputChange}
                error={errorMessage && penaltyDetails.ceilingValue <= 0}
                errorMessage={t('required')}
                disabled={!isEdit}
                valueRequired
                disallowNegative
              />
            </div>
            <MultiSelectDropdown
              name="triggerMessage"
              title={t('triggerMessage')}
              selectedValues={penaltyDetails.triggerMessage}
              onSelectionChange={handleInputChange}
              placeholder={t('selectOption')}
              placeholderFilled={t('options')}
              options={[
                { label: t('suspendedEvse'), value: 'SuspendedEVSE' },
                { label: t('suspendedEv'), value: 'SuspendedEV' },
                { label: t('faulted'), value: 'Faulted' },
                { label: t('finishing'), value: 'Finishing' },
              ]}
              search={false}
              multiSelectDisable={false}
              valueRequired
              fontSize="small"
              selectedChips
              error={errorMessage && penaltyDetails.triggerMessage.length === 0}
              errorMessage={t('required')}
              disabled={!isEdit}
            />
            <div className="date-box-content">
              <CustomDatePicker
                name="effectiveFrom"
                inputLabel={t('startDate')}
                placeholder={t('enterValue')}
                value={penaltyDetails.effectiveFrom}
                onChangeValue={handleInputChange}
                error={Boolean(
                  errorMessage && (penaltyDetails.effectiveFrom === '' || isDateInvalid),
                )}
                errorMessage={
                  penaltyDetails.effectiveFrom === ''
                    ? t('startDateRequired')
                    : t('startDateGreaterError') // add this translation
                }
                readOnly={!isEdit}
                valueRequired
                disablePastDates
                minDate={penaltyDetails.effectiveFrom}
              />{' '}
              <CustomDatePicker
                name="effectiveEnd"
                inputLabel={t('endDate')}
                placeholder={t('enterValue')}
                value={penaltyDetails.effectiveEnd}
                onChangeValue={handleInputChange}
                error={Boolean(
                  errorMessage && (penaltyDetails.effectiveEnd === '' || isDateInvalid),
                )}
                errorMessage={
                  penaltyDetails.effectiveEnd === '' ? t('endDateRequired') : t('endDateLessError')
                }
                readOnly={!isEdit}
                valueRequired
                disablePastDates
                minDate={penaltyDetails.effectiveFrom}
              />
            </div>
          </div>
          <div className="pricing-wrap">
            {penaltyDetails.durationAmountList.map((rule, index: number) => (
              <div className="rule-box-container">
                <div className="rule-box" key={rule.from}>
                  <CustomInput
                    name="from"
                    inputLabel={t('fromMins')}
                    placeholder={t('enterFromMins')}
                    type="number"
                    value={rule.from}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof DurationAmountListInterface, String(value))
                    }
                    error={ruleErrors[index]?.from}
                    errorMessage={t('invalidRange')}
                    disabled={!isEdit}
                    disallowNegative
                  />{' '}
                  <CustomInput
                    name="to"
                    inputLabel={t('toMins')}
                    placeholder={t('enterToMins')}
                    type="number"
                    value={rule.to}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof DurationAmountListInterface, String(value))
                    }
                    error={ruleErrors[index]?.to}
                    errorMessage={t('invalidRange')}
                    disabled={!isEdit}
                    disallowNegative
                  />
                  <SelectFiled
                    name="mode"
                    inputLabel={t('mode')}
                    placeholder={t('selectMode')}
                    selectedValue={rule.mode}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof DurationAmountListInterface, String(value))
                    }
                    options={getAvailableModes(index)}
                    disabled={!isEdit}
                  />
                  <CustomInput
                    name="amount"
                    inputLabel={t('amount')}
                    placeholder={t('enterValue')}
                    type="number"
                    value={rule.amount}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof DurationAmountListInterface, String(value))
                    }
                    disabled={!isEdit || rule.mode === 'grace'}
                    disallowNegative
                  />
                  {type !== 'approved' && type !== 'rejected' && type !== 'pending' && (
                    <div className="delete-button">
                      {penaltyDetails.durationAmountList.length > 1 && (
                        <MyButton
                          label=""
                          svgIcon={<DeleteIcon />}
                          buttonType="error"
                          onClickFunc={() => deleteRule(index)}
                          // disableButton={buttonStatus}
                        />
                      )}
                    </div>
                  )}
                </div>
                {isEdit && (
                  <div className="add-button">
                    {index === penaltyDetails.durationAmountList.length - 1 && (
                      <MyButton
                        label={t('add')}
                        svgIcon={<PlusIcon />}
                        buttonType="primary-outline"
                        onClickFunc={addRule}
                        disableButton={buttonStatus}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {type === 'rejected' && !isEdit && (
            <div>
              <RejectReasonField
                label={t('rejectReason')}
                placeholder={t('writeReason')}
                value={rejectReason}
                onChange={(value) => {
                  setRejectReason(value);
                }}
                mode="view"
              />
            </div>
          )}
          {/* {type === 'Pending' && (
                <>
                  <div>
                    <RejectReasonField
                      label="If Reject (Reason for reject)"
                      placeholder="write Reason..."
                      value={rejectReason}
                      onChange={(value) => {
                        setRejectReason(value);
                        if (rejectError) setRejectError('');
                      }}
                      error={rejectError}
                      mode="add"
                    />
                  </div>
                  <div className="button-row">
                    <MyButton
                      label="Reject"
                      buttonType="error"
                      onClickFunc={() => {
                        if (!rejectReason.trim()) {
                          setRejectError('Reason is required');
                          return;
                        }

                        handleActionReject(rejectReason, currentData);
                        closeModal(rejectReason);
                      }}
                      disableButton={buttonStatus}
                    />
                    <MyButton
                      label="Approve"
                      buttonType="submit"
                      onClickFunc={() => handleActionApprove(currentData._id)}
                      loading={buttonStatus}
                    />
                  </div>
                </>
              )} */}
          {isEdit && (
            <div className="button-row">
              <MyButton
                label={t('cancel')}
                buttonType="error"
                onClickFunc={() => closeModal('cancel')}
              />
              <MyButton label={t('submit')} buttonType="submit" onClickFunc={handleSubmit} />
            </div>
          )}
        </div>
        <div className="AuditLog">
          {currentData._id === 'okay' && (
            <AuditLog
              currentData={{
                id: currentData._id,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

ViewOverrides.defaultProps = {
  currentData: null,
  hasEdit: false,
  handleRefresh: null,
};
export default ViewOverrides;
